import { ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import cn from 'classnames'
import { ContentSteps, useStepFlow } from 'src/lib/useStepFlow'
import {
    FirstStep,
    SecondStep,
    TFirstStepProps,
    TSecondStepProps,
} from './ui/steps'
import Logo from 'src/icons/logo.webp'
import Monitor from 'src/icons/monitor.webp'
import Ray from 'src/icons/ray.webp'
import Hand from 'src/icons/hand.webp'
import { textBtnFirst } from './ui/steps/first-step/mock'
import { textBtnSecond } from './ui/steps/second-step/mock'
import { CustomButton } from './ui/button/CustomButton'

import { useControlModal, ModalType } from '@rockstar/registration-component'
import { SignUp } from 'src/features/registration/SignUp'
import { normalizeBrowserLang, TLang } from 'src/constants/langs'
import styles from './styles.module.scss'

const stepComponents: {
    [ContentSteps.FIRST]: React.ComponentType<TFirstStepProps>
    [ContentSteps.SECOND]: React.ComponentType<TSecondStepProps>
} = {
    [ContentSteps.FIRST]: FirstStep,
    [ContentSteps.SECOND]: SecondStep,
}

const Aviator = () => {
    const { modalType, setModalType } = useControlModal()
    const { step, goToNextStep } = useStepFlow()
    const [isStoppedCounter, setIsStoppedCounter] = useState<boolean>(false)
    const [isClick, setIsClick] = useState<boolean>(false)
    const btnRef = useRef<HTMLImageElement>(null)

    const currentLang = useMemo<TLang>(() => {
        const nav = typeof navigator !== 'undefined' ? navigator.language : 'en'
        return normalizeBrowserLang(nav)
    }, [])

    const stepTextMap: Record<ContentSteps, { lang: TLang; text: string }[]> = {
        [ContentSteps.FIRST]: textBtnFirst,
        [ContentSteps.SECOND]: textBtnSecond,
    }

    const getBtnTextByLang = (step: ContentSteps, currentLang: TLang): string =>
        stepTextMap[step]?.find((item) => item.lang === currentLang)?.text ?? ''

    const handleStoppedCounter = () => {
        setIsStoppedCounter(true)
    }

    const MODALS = useMemo<{ [key in ModalType]: ReactNode | null }>(
        () => ({
            sign_up: <SignUp language={currentLang} />,
            clear: null,
        }),
        [currentLang],
    )

    const handleClick = () => {
        if (step === ContentSteps.SECOND) {
            setModalType('sign_up')
            return
        }

        const btn = btnRef.current

        if (btn) {
            const style = window.getComputedStyle(btn)
            const matrix = new DOMMatrixReadOnly(style.transform)
            const currentScale = matrix.a

            btn.style.animation = 'none'
            btn.style.transform = `translateX(-50%) scale(${currentScale})`

            requestAnimationFrame(() => {
                btn.style.transition = 'transform 0.5s ease-in-out'
            })
        }
        setIsStoppedCounter(true)
        setIsClick(true)
    }

    useEffect(() => {
        const btn = btnRef.current
        if (btn) {
            btn.style.transform = ''
            btn.style.transition = ''
            btn.style.animation = ''
        }
    }, [step])

    const renderStep = () => {
        switch (step) {
            case ContentSteps.FIRST:
                const First = stepComponents[ContentSteps.FIRST]
                return (
                    <First
                        onNext={goToNextStep}
                        isStoppedCounter={isStoppedCounter}
                        handleStoppedCounter={handleStoppedCounter}
                        step={step}
                    />
                )
            case ContentSteps.SECOND:
                const Second = stepComponents[ContentSteps.SECOND]
                return <Second lang={currentLang} />
        }
    }

    return (
        <div
            className={cn(styles.root, {
                [styles['root--el']]: currentLang === 'el',
            })}
        >
            <img src={Logo} alt="Logo" className={styles.logo} />
            <div className={styles.container}>
                <div className={styles.ray_wrapper}>
                    <img
                        src={Ray}
                        alt="Ray"
                        className={cn(styles.ray, {
                            [styles.disappearance]:
                                step === ContentSteps.FIRST && isStoppedCounter,
                        })}
                    />
                </div>

                <div className={styles.screen_wrapper}>{renderStep()}</div>
                {isStoppedCounter && <div className={styles.bkg} />}
                <img src={Monitor} alt="Monitor" className={styles.monitor} />

                <div
                    ref={btnRef}
                    className={cn(
                        styles.wrapper_button,
                        styles[`wrapper_button__${step}`],
                        {
                            [styles.btn_pulse]:
                                step === ContentSteps.FIRST &&
                                !isStoppedCounter,
                        },
                    )}
                    onClick={
                        (step === ContentSteps.FIRST && !isStoppedCounter) ||
                        step === ContentSteps.SECOND
                            ? handleClick
                            : undefined
                    }
                >
                    <CustomButton
                        textBtn={getBtnTextByLang(step, currentLang)}
                        classNames={{ textBtn: styles[`textBtn__${step}`] }}
                        disabled={
                            step === ContentSteps.FIRST &&
                            (isStoppedCounter || isClick)
                        }
                    />
                </div>
                <div
                    className={cn(styles.hand_wrapper, {
                        [styles.hidden_hand]:
                            (step === ContentSteps.FIRST && isStoppedCounter) ||
                            modalType === 'sign_up',
                    })}
                >
                    <img src={Hand} alt="Hand" className={styles.hand} />
                </div>
            </div>
            {MODALS[modalType]}
        </div>
    )
}

export default Aviator
